import { useState, type ReactNode } from "react";
import { Check, Copy, Landmark } from "lucide-react";
import { formatIDR } from "@kanata/shared";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

/**
 * A branded virtual account (DEL-439): the bank, the number and the exact
 * amount, each one tap to copy, on Kanata's own page.
 *
 * The buyer pays from their bank's app and comes back to this tab, which
 * polls like the QRIS page and turns to "paid" by itself. Staying on Kanata is
 * the point: the hosted page this replaces never sent anybody back.
 */
export function VaInstructions({
  bankName,
  accountNumber,
  totalIdr,
  expiry,
  waiting,
  fake,
  referenceNo,
}: {
  bankName: string;
  accountNumber: string;
  totalIdr: number;
  /** The expiry line, the same element the QRIS page shows. */
  expiry: ReactNode;
  /** The "waiting for payment" line. */
  waiting: ReactNode;
  fake?: boolean;
  referenceNo?: string;
}) {
  return (
    <div className="rounded-2xl border border-ink/8 bg-cream/60 p-6 sm:p-8">
      <div className="text-center">
        <p className="text-sm font-medium tracking-wide text-brand uppercase">Virtual Account</p>
        <h2 className="mt-1 text-2xl font-medium tracking-tight text-ink">Transfer ke {bankName}</h2>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-ink/60">
          Bayar dari m-banking, internet banking, atau ATM {bankName}. Nominalnya harus sama persis.
        </p>
      </div>

      {fake && (
        <div className="mx-auto mt-6 max-w-sm rounded-xl border border-amber-400 bg-amber-50 p-3 text-left text-xs text-amber-900">
          <p className="font-semibold">Nomor VA contoh — tidak bisa dibayar.</p>
          <p className="mt-1">
            Server ini berjalan tanpa kunci Altura. Setel <code className="font-mono">ALTURA_API_KEY</code> lalu ulangi
            checkout.
          </p>
        </div>
      )}

      <div className="mt-6 space-y-3 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ink/5">
        <CopyRow
          label={`Nomor virtual account ${bankName}`}
          value={accountNumber}
          display={groupDigits(accountNumber)}
          big
        />
        <Separator />
        <CopyRow label="Total transfer" value={String(totalIdr)} display={formatIDR(totalIdr)} />
      </div>

      <div className="mt-2 text-center">{expiry}</div>

      <ol className="mt-6 space-y-2 text-sm text-ink/70">
        {[
          `Buka aplikasi ${bankName} atau datang ke ATM ${bankName}.`,
          "Pilih menu Transfer, lalu Virtual Account.",
          "Masukkan nomor VA di atas.",
          "Pastikan nominalnya sama, lalu konfirmasi.",
        ].map((step, i) => (
          <li key={step} className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-light text-xs font-medium text-brand-dark">
              {i + 1}
            </span>
            <span className="pt-0.5">{step}</span>
          </li>
        ))}
      </ol>

      <Separator className="my-6" />
      <div className="flex items-center justify-center gap-2 text-center text-sm text-ink/55">{waiting}</div>
      <p className="mt-4 flex items-start justify-center gap-2 text-center text-xs leading-relaxed text-ink/45">
        <Landmark className="mt-0.5 size-3.5 shrink-0" />
        <span>Setelah transfer, kembali ke halaman ini. Statusnya berubah sendiri dalam beberapa saat.</span>
      </p>
      {referenceNo && <p className="mt-4 text-center text-xs text-ink/35">Ref. {referenceNo}</p>}
    </div>
  );
}

/** "7001400009085556" → "7001 4000 0908 5556": easier to read out and to check. */
export function groupDigits(n: string): string {
  return n.replace(/(\d{4})(?=\d)/g, "$1 ");
}

function CopyRow({ label, value, display, big }: { label: string; value: string; display: string; big?: boolean }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard: the number is on screen to type.
    }
  }
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs text-ink/55">{label}</p>
        <p
          className={
            big
              ? "mt-1 font-mono text-xl font-semibold tracking-wide text-ink sm:text-2xl"
              : "mt-1 text-lg font-medium text-ink tabular-nums"
          }
        >
          {display}
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={() => void copy()} aria-label={`Salin ${label}`}>
        {copied ? <Check /> : <Copy />}
        {copied ? "Tersalin" : "Salin"}
      </Button>
    </div>
  );
}
